import React from "react";
import {
  FiDatabase,
  FiSearch,
  FiTrendingUp,
  FiBarChart2,
  FiLayers,
  FiGitBranch,
} from "react-icons/fi";
import { MdInsights, MdOutlinePattern } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const DataIntelligenceAgent = () => {
  const features = [
    {
      title: "Automated Data Extraction",
      description:
        "Agents pull structured and unstructured data from APIs, documents, and data lakes with validation checkpoints.",
      icon: <FiDatabase />,
    },
    {
      title: "Exploratory Data Analysis",
      description:
        "AI inspects datasets, detects anomalies, and surfaces statistical summaries or correlations automatically.",
      icon: <FiSearch />,
    },
    {
      title: "Pattern Detection",
      description:
        "Advanced ML models identify behavioral, operational, or financial patterns across high-volume signals.",
      icon: <MdOutlinePattern />,
    },
    {
      title: "Predictive Insights",
      description:
        "Predictive pipelines forecast demand, churn, risk, or maintenance windows with explainable outputs.",
      icon: <FiTrendingUp />,
    },
    {
      title: "Narrative Analytics",
      description:
        "LLM-generated narratives translate numbers into executive-ready stories, alerts, and recommendations.",
      icon: <MdInsights />,
    },
    {
      title: "Decision Orchestration",
      description:
        "Agents trigger downstream workflows, update dashboards, or notify teams when KPIs breach thresholds.",
      icon: <FiGitBranch />,
    },
  ];

  const steps = [
    {
      title: "Data Landscape Audit",
      description:
        "Map current data sources, governance policies, and analytical objectives to scope the agent footprint.",
    },
    {
      title: "Pipeline Engineering",
      description:
        "Set up ingestion, transformation, and storage layers with quality checks and metadata tracking.",
    },
    {
      title: "Model & Agent Build",
      description:
        "Train ML models, configure insight agents, and build prompts that align outputs with business KPIs.",
    },
    {
      title: "Insight Activation",
      description:
        "Deploy dashboards, alerts, and workflow triggers that push insights into daily decision-making.",
    },
  ];

  const benefits = [
    "Reduce manual analysis time with autonomous insight generation",
    "Gain faster visibility into anomalies and leading indicators",
    "Improve forecasting accuracy with continuously trained models",
    "Break down silos by blending operational and customer data",
    "Empower teams with narrative briefings tailored to their role",
    "Activate data by triggering actions in CRM, ERP, or BI tools",
  ];

  const expertise = [
    "Modern data stack expertise across Snowflake, BigQuery, Databricks, and Redshift",
    "Data engineering accelerators for ELT/ETL, dbt, and orchestration",
    "ML Ops pipelines with MLflow, Vertex AI, SageMaker, and Azure ML",
    "Explainable AI frameworks to meet regulatory expectations",
    "BI integrations with Looker, Power BI, Tableau, and Mode",
    "Data governance specialists for lineage, catalog, and quality",
  ];

   const image = [
    {
      side_image: "../assets/images/ai-automation/7.png",
    }

  ];

  const useCases = [
    {
      industry: "Retail",
      title: "Demand Intelligence Agent",
      description:
        "Combines POS, marketing, and supply data to forecast demand and trigger replenishment workflows.",
    },
    {
      industry: "Energy",
      title: "Asset Performance Monitor",
      description:
        "Analyzes IoT signals to predict outages, optimize maintenance, and alert field teams.",
    },
    {
      industry: "Fintech",
      title: "Risk Analytics Copilot",
      description:
        "Monitors transactions, flags anomalies, and drafts compliance summaries for risk teams.",
    },
    {
      industry: "Healthcare",
      title: "Operational Intelligence",
      description:
        "Aggregates EHR, staffing, and supply data to optimize staffing and reduce wait times.",
    },
  ];

  const reasons = [
    "Data strategists, engineers, and data scientists under one roof",
    "Blueprints for data quality, lineage, and governance baked into delivery",
    "Reusable feature stores and model templates accelerate time-to-value",
    "Security and compliance expertise for regulated data estates",
    "Co-managed operations for monitoring, retraining, and support",
    "Collaborative engagement with transparent ROI tracking",
  ];

  return (
    <AIAutomationTemplate
      title="Data Intelligence Agent"
      heroDescription="Transform raw data into continuous intelligence with agents that extract, analyze, and activate insights."
      about={{
        heading: "About Data Intelligence Agent Services",
        description:
          "We combine modern data platforms, ML, and narrativized analytics to deliver insights that trigger decisions automatically.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Unlock Data Intelligence"
      meta={{
        title: "Data Intelligence Agent | Predictive Analytics Services | Ayanshtech",
        description:
          "Data extraction, analysis, pattern detection, and predictive insight agents for faster decisions.",
        ogTitle: "Data Intelligence Agent Solutions",
        ogDescription:
          "Autonomous agents that extract, analyze, and activate data-driven insights.",
        ogUrl: "http://ayanshtechnology.com/data-intelligence-agent",
        canonical: "http://ayanshtechnology.com/data-intelligence-agent",
      }}
    />
  );
};

export default DataIntelligenceAgent;


